import { ColorPill } from '@/src/modules/searchBar/components/ColorPill';
import { SearchBarActionsProvider } from '@/src/modules/searchBar/components/SearchBarActionsProvider';
import { useQueryInbox } from '@fabric-app/business/connections/queries/useQueryInbox';
import { useQueryResourceRoot } from '@fabric-app/business/resource-roots/queries/useQueryResourceRoot';
import { ResourceListContextPreferencesProvider } from '@fabric-app/business/resources/components/ResourceList/contexts/resourceListContextPreferences';
import { useQueryFolder } from '@fabric-app/business/resources/queries/useQueryFolder';
import { IconNavigational24Cross } from '@fabric-app/icons/svg';
import { Button } from '@fabric-app/ui/components/Button';
import { Flex } from '@fabric-app/ui/components/Flex';
import { useIsAnyModalOpen } from '@fabric-app/ui/components/Modal/useIsAnyModalOpen';
import { Overlay } from '@fabric-app/ui/components/Overlay';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { cssVar } from '@fabric-app/ui/theme/variables';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'styled-components';
import useGlobalSearchStore, {
  defaultSearchBarValue,
} from '@fabric-app/business/search/hooks/hybridsearch';
import { useSharedHover } from '../../hooks/sharedHover';
import { RecentSearch } from '../../modules/search/components/RecentSearch/RecentSearch';
import { FilterPills } from '../../modules/searchBar/components/FilterPills';
import type { SearchBarRef } from '../../modules/searchBar/components/SearchBar';
import SearchBar from '../../modules/searchBar/components/SearchBar';
import { SearchBarFilterType } from '@fabric-app/business/search/searchbar.types';
import { DashboardHeader } from '@fabric-app/business/dashboard/components/DashboardHeader';
import { DashboardContent } from '@fabric-app/business/dashboard/components/DashboardContent';
import { useViewPreferences } from './useViewPreferences';
import { useSearchOverlay } from '@fabric-app/business/search/hooks/useSearchOverlay';
import { listPreferenceOrderToSearchSort } from '@fabric-app/business/resources/utils/listPreferenceOrderToSearchSort';
import { useRouter } from '@fabric-app/compat';
import { createLogger } from '@fabric-app/utils/logging';
import { motion, AnimatePresence } from 'framer-motion';
import { ZoomSliderWithinDropdownV2 } from '../ZoomSlider/ZoomSliderWithinDropdown';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { ViewModeSwitcher } from '@fabric-app/business/resources/components/ResourceList/ViewModeSwitcher/ViewModeSwitcher';
import { SearchResultsDesktop } from './SearchResults/SearchResultsDesktopLayout';
import { SearchResultsMobile } from './SearchResults/SearchResultsMobile';
import { ResourceUiDropdownShowControls } from '@fabric-app/business/resources/components/DropdownShowControls';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { useAdminSettings } from './useAdminSettings';

import { DEFAULT_SEARCH_VIEW_MODE, SEARCH_VIEW_MODES } from './constants';
import { useFabricEvent } from '@fabric-app/business/hooks/useFabricEvent';
import {
  useExpandedResource,
  useShowExpandedResource,
} from '@fabric-app/business/resources/stores/useExpandedResourceStore';
import { useQueryIntegrations } from '@fabric-app/business/connections/queries/useQueryIntegrations';
import { getRouteParamAsString } from '@fabric-app/utils/url/routing';

const logger = createLogger('SearchBarWithOverlay', true);

const emptyStateImage = <img src="/images/empty-search.png" alt="empty-search" />;

const SearchBarWithOverlay: React.FC = () => {
  const { searchArgs, dropdownControls } = useAdminSettings();
  const debouncedSearchArgs = useDebounce(searchArgs, 500);
  /*****************/

  const router = useRouter();
  const [viewportRef, setViewportRef] = useState<HTMLDivElement | null>(null);
  const searchBarRef = useRef<SearchBarRef>(null);

  const {
    viewPreferences,
    isLoading: isPreferencesLoading,
    saveViewPreferences,
  } = useViewPreferences();

  const { integrations } = useQueryIntegrations();
  const hasSearchBasedIntegrations = integrations.some(
    (integration) => integration.viewConfig.isSearchBased,
  );

  const {
    closeOverlay,
    isOverlayOpen: isSearchOverlayOpen,
    openOverlay,
    hideExternalSearch,
    setHideExternalSearch,
  } = useSearchOverlay();
  const resourceId = useExpandedResource();
  const showExpandedResource = useShowExpandedResource();

  useFabricEvent('fabric-search-action', ({ detail }) => {
    if (detail.type !== 'search' || detail.query.trim() === '') return;

    const openedResource = resourceId;
    showExpandedResource(null); // hide expanded resource, as it goes on top of overlay

    setSearchBarValue({
      ...defaultSearchBarValue,
      query: detail.query,
    });

    openOverlay('global', {
      onCloseOverlayOnce: () => {
        // reset search data
        setSearchBarValue(defaultSearchBarValue);

        if (!openedResource) return;
        showExpandedResource(openedResource);
      },
    });
  });

  /**
   * autoclose when route changes excluding search params
   */
  useEffect(() => {
    let currentPathname = router.asPath.split('?')[0];
    const routeChange = (pathname: unknown) => {
      if (typeof pathname !== 'string') return;

      const nextPathname = pathname.split('?')[0];
      if (nextPathname === currentPathname) return;

      logger.log(`Autoclose, changing route from ${currentPathname} to ${nextPathname}`);

      currentPathname = nextPathname;

      closeOverlay();
    };
    router.events.on('routeChangeComplete', routeChange);
    return () => {
      router.events.off('routeChangeComplete', routeChange);
    };
  }, []);

  const {
    searchBarValue,
    setSearchBarValue,
    isLoading,
    resources,
    selectedIndex,
    fetchNextPage: _fetchNextPage,
    isFetchingNextPage,
    zoomLevel,
    setZoomLevel,
    isSearchBarEmpty,
    total,
    hasNextPage,
    queryHash,
    filters,
    searchQueryArgs,
  } = useGlobalSearchStore({
    _id: 'web',
    viewportRef,
    searchArgs: debouncedSearchArgs,
    disabled: !isSearchOverlayOpen,
    sort: viewPreferences?.filters.order
      ? listPreferenceOrderToSearchSort(viewPreferences.filters.order)
      : undefined,
  });

  const [initLocationFilter, setInitLocationFilter] = useState(false);

  useEffect(() => {
    if (!isSearchOverlayOpen || router.query.search !== 'location') return;

    setInitLocationFilter(true);
  }, [isSearchOverlayOpen, router.query.search]);

  const [folderIdParam, fidParam, spaceId] = getRouteParamAsString(router.query, [
    'folderId',
    'fid',
    'listId',
  ]);

  const folderId = folderIdParam ?? fidParam;

  const { inboxRoot } = useQueryInbox();
  const { folder } = useQueryFolder({ resourceId: folderId });

  const { asLegacySpace: space } = useQueryResourceRoot(spaceId);

  useEffect(() => {
    if (!initLocationFilter) return;
    setInitLocationFilter(false);

    if (router.pathname === '/saved' && inboxRoot?.id) {
      setSearchBarValue((searchBarValue) => ({
        ...searchBarValue,
        filters: [
          {
            $key: 'overlay-location',
            type: SearchBarFilterType.LOCATION,
            value: {
              label: 'Inbox',
              value: inboxRoot?.id,
            },
          },
        ],
      }));
      return;
    }

    if (typeof folderId === 'string' && folder) {
      setSearchBarValue((searchBarValue) => ({
        ...searchBarValue,
        filters: [
          {
            $key: 'overlay-location',
            type: SearchBarFilterType.LOCATION,
            value: {
              label: folder.name || 'Untitled folder',
              value: folderId,
            },
          },
        ],
      }));
      return;
    }

    if (typeof spaceId === 'string' && space) {
      setSearchBarValue((searchBarValue) => ({
        ...searchBarValue,
        filters: [
          {
            $key: 'overlay-location',
            type: SearchBarFilterType.LOCATION,
            value: {
              label: space.title,
              value: spaceId,
            },
          },
        ],
      }));
      return;
    }
  }, [
    initLocationFilter,
    folderId,
    inboxRoot?.id,
    router.pathname,
    router.query,
    setSearchBarValue,
    spaceId,
    folder,
    space,
  ]);

  useEffect(() => {
    if (isSearchOverlayOpen) return;
    /**
     * When closing the overlay
     */

    /**
     * reset the filters
     */
    setSearchBarValue(defaultSearchBarValue);
  }, [isSearchOverlayOpen, setSearchBarValue]);

  const { isMobileView } = useResponsive();

  const modalOpened = useIsAnyModalOpen();

  // add escape to go to /
  useEffect(() => {
    if (modalOpened) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeOverlay();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [modalOpened, router, closeOverlay]);

  const shouldAutoFocus = !modalOpened && !isMobileView;

  const focusSearchbar = React.useCallback(() => {
    if (!shouldAutoFocus) return;

    window.requestAnimationFrame(() => {
      searchBarRef.current?.focus();
    });
  }, [shouldAutoFocus]);

  useEffect(() => {
    if (modalOpened) return;
    focusSearchbar();
  }, [modalOpened, focusSearchbar]);

  const { setHover, hoverId } = useSharedHover();
  useEffect(() => {
    if (selectedIndex === null) return;

    setHover(resources[selectedIndex]?.id ?? null, true);
  }, [selectedIndex, resources, setHover]);

  useEffect(() => {
    if (selectedIndex !== null) return;

    setHover(hoverId ?? '', false);
  }, [hoverId, selectedIndex, setHover]);

  useEffect(() => {
    if (selectedIndex === null || hoverId !== resources[selectedIndex]?.id) return;

    const timeout = setTimeout(() => {
      const el = document.querySelector(`[data-selectable-id="${hoverId}"]`);
      if (!el) return;

      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 10);

    return () => {
      clearTimeout(timeout);
    };
  }, [hoverId, selectedIndex, resources]);

  const emptyStateTitle = useMemo(() => {
    if (!isSearchBarEmpty) return 'No matches';
    return undefined;
  }, [isSearchBarEmpty]);

  const emptyStateReason = useMemo(() => {
    // If the search input is empty but there are filters:
    if (searchBarValue.query === '' && !isSearchBarEmpty) return 'No results found.';
    // Search input has a value:
    if (!isSearchBarEmpty) return `No results for "${searchBarValue.query}"`;
    return undefined;
  }, [searchBarValue.query, isSearchBarEmpty]);

  const listBoxRef = React.useRef<HTMLDivElement | null>(null);
  const fetchNextPage =
    isFetchingNextPage || isLoading || !hasNextPage ? undefined : _fetchNextPage;

  const searchResultProps = {
    isLoading: isLoading,
    resources: resources,
    queryArgs: searchQueryArgs.params as any,
    total: total,
    fetchNextPage: fetchNextPage,
    queryHash: queryHash,
    emptyStateImage: emptyStateImage,
    emptyStateTitle: emptyStateTitle,
    emptyStateReason: emptyStateReason,
    zoomLevel,
    setZoomLevel,
    isSaveAsSmartCollectionEnabled:
      isSearchOverlayOpen && !isSearchBarEmpty && resources.length > 0 && !searchBarValue.imageUrl,
    filters: filters,
  };

  return (
    <ResourceListContextPreferencesProvider
      preferences={viewPreferences}
      savePreferences={saveViewPreferences}
      /**
       * search currently doesn't support sort by name/title
       */
      disableSort
      defaultViewMode={DEFAULT_SEARCH_VIEW_MODE}
      availableViewModes={SEARCH_VIEW_MODES}
    >
      {({ viewMode }) => (
        <Overlay
          style={{
            paddingBottom: 0,
          }}
          key="global-search-overlay"
          animate={isSearchOverlayOpen ? 'open' : 'close'}
          initial={{
            opacity: 0,
          }}
          variants={{
            open: { opacity: 1, pointerEvents: 'auto' },
            close: { opacity: 0, pointerEvents: 'none' },
          }}
        >
          <SearchBarActionsProvider listBoxRef={listBoxRef}>
            <DashboardHeader
              style={{ overflow: 'initial', background: cssVar['color-bg-primary'] }}
            >
              <DashboardHeader.Body style={{ overflow: 'initial', minHeight: 72 }}>
                <Flex alignItems="center" gap={12}>
                  <Button variant="bg-secondary" onClick={closeOverlay} shape="square">
                    <IconNavigational24Cross />
                  </Button>

                  {isSearchOverlayOpen ? (
                    <SearchBar
                      listBoxRef={listBoxRef}
                      value={searchBarValue}
                      setValue={setSearchBarValue}
                      ref={searchBarRef}
                      onBlur={shouldAutoFocus ? focusSearchbar : undefined}
                      autoFocus
                      isLoading={isLoading}
                    />
                  ) : (
                    <div
                      style={{
                        flexGrow: 1,
                        height: 46.7,
                      }}
                    />
                  )}
                  {(!isMobileView && isSearchOverlayOpen) || dropdownControls !== null ? (
                    <Flex
                      as={motion.div}
                      alignItems="center"
                      gap={12}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                      exit={{ opacity: 0 }}
                    >
                      <AnimatePresence>
                        <ViewModeSwitcher key="switcher" />
                        {(viewMode !== 'List' || hasSearchBasedIntegrations) && (
                          <DropdownMenu.Base
                            key="dropdown"
                            renderTriggerElement={({ onOpenChange }) => (
                              <DropdownMenu.TriggerButtonThreeDots onOpenChange={onOpenChange} />
                            )}
                            contentProps={{
                              style: {
                                maxHeight: '80vh',
                              },
                            }}
                          >
                            <DropdownMenu.Group>
                              <DropdownMenu.ToggleItem
                                onClick={() => {
                                  setHideExternalSearch(!hideExternalSearch);
                                }}
                              >
                                <span>Show external search panel</span>{' '}
                                <DropdownMenu.ToggleItemSwitch checked={!hideExternalSearch} />
                              </DropdownMenu.ToggleItem>
                            </DropdownMenu.Group>
                            <DropdownMenu.Separator />
                            {!isMobileView && ['Grid', 'Masonry'].includes(viewMode) && (
                              <DropdownMenu.Group>
                                <ResourceUiDropdownShowControls uiContext="global" />
                              </DropdownMenu.Group>
                            )}
                            <ZoomSliderWithinDropdownV2
                              onChange={setZoomLevel}
                              value={zoomLevel}
                              viewMode={viewMode}
                            />
                            {dropdownControls}
                          </DropdownMenu.Base>
                        )}
                      </AnimatePresence>
                    </Flex>
                  ) : null}
                </Flex>
              </DashboardHeader.Body>
              {searchBarValue.filters.length > 0 || searchBarValue.color ? (
                <ScrollArea.Default scrollbarVariant="none" orientation="horizontal">
                  <FilterBottomContent data-search-bar-filters-container>
                    <Flex gap={12}>
                      {searchBarValue.color && (
                        <ColorPill
                          color={searchBarValue.color}
                          resetColor={() => {
                            setSearchBarValue((prevState) => {
                              return {
                                ...prevState,
                                color: undefined,
                              };
                            });
                          }}
                        />
                      )}
                      <FilterPills value={searchBarValue} setValue={setSearchBarValue} />
                    </Flex>
                  </FilterBottomContent>
                </ScrollArea.Default>
              ) : null}
            </DashboardHeader>
          </SearchBarActionsProvider>

          {isSearchOverlayOpen && !isPreferencesLoading ? (
            isSearchBarEmpty ? (
              <DashboardContent
                style={{
                  pointerEvents: isLoading ? 'none' : undefined,
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'opacity 0.2s ease-in-out',
                }}
                viewportRef={setViewportRef}
              >
                <RecentSearch
                  key="recent-search"
                  onSelect={(val) =>
                    setSearchBarValue((searchBarValue) => ({
                      ...searchBarValue,
                      query: val,
                      imageUrl: undefined,
                    }))
                  }
                />
              </DashboardContent>
            ) : isMobileView ? (
              <SearchResultsMobile {...searchResultProps} />
            ) : (
              <SearchResultsDesktop {...searchResultProps} />
            )
          ) : null}
        </Overlay>
      )}
    </ResourceListContextPreferencesProvider>
  );
};

export default SearchBarWithOverlay;

const FilterBottomContent = styled(DashboardHeader.Body)`
  border-top: 1px solid ${cssVar['color-border-primary']};
  padding-top: 12px;
  padding-bottom: 12px;
  height: auto;
  min-height: 40px;
`;
